/**
 * Structured logger for frontend application.
 * Provides consistent logging with levels and metadata.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
}

// Log level priority (lower = more verbose)
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Get configured log level from environment or default to 'info' in prod, 'debug' in dev
const getConfiguredLevel = (): LogLevel => {
  // In production, default to 'warn' to reduce noise
  // In development, default to 'debug' for full visibility
  const isDev = import.meta.env.DEV;
  const envLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined;
  return envLevel || (isDev ? "debug" : "warn");
};

const configuredLevel = getConfiguredLevel();

/**
 * Check if a log level should be output based on configuration
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[configuredLevel];
}

/**
 * Format a log entry for console output
 */
function formatEntry(entry: LogEntry): string {
  const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : "";
  return `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}${contextStr}`;
}

/**
 * Core logging function
 */
function log(level: LogLevel, message: string, context?: LogContext): void {
  if (!shouldLog(level)) return;

  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    context,
  };

  // Use appropriate console method
  const consoleMethod = level === "debug" ? "log" : level;

  // In development, use formatted string for readability
  // In production, output structured JSON
  if (import.meta.env.DEV) {
    console[consoleMethod](formatEntry(entry));
  } else {
    console[consoleMethod](JSON.stringify(entry));
  }
}

/**
 * Logger interface for application use
 */
export const logger = {
  debug: (message: string, context?: LogContext) => log("debug", message, context),
  info: (message: string, context?: LogContext) => log("info", message, context),
  warn: (message: string, context?: LogContext) => log("warn", message, context),
  error: (message: string, context?: LogContext) => log("error", message, context),
};

/**
 * Create a child logger with preset context
 * Useful for adding component or feature context to all logs
 *
 * @example
 * const log = createLogger({ component: "AppDetailPage" });
 * log.info("Loading app details", { appId: "123" });
 */
export function createLogger(baseContext: LogContext) {
  return {
    debug: (message: string, context?: LogContext) =>
      log("debug", message, { ...baseContext, ...context }),
    info: (message: string, context?: LogContext) =>
      log("info", message, { ...baseContext, ...context }),
    warn: (message: string, context?: LogContext) =>
      log("warn", message, { ...baseContext, ...context }),
    error: (message: string, context?: LogContext) =>
      log("error", message, { ...baseContext, ...context }),
  };
}

export default logger;
